import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ApplyFallback, ApplyRow, RouteRef, TestsApi } from '../../services/testsApi';
import { modelLabel } from './common';
import { errorText } from './dialogs';

const FALLBACK_OPTIONS: { value: ApplyFallback; label: string; help: string }[] = [
  { value: 'budget', label: 'Budget', help: 'The cheapest usable model other than Recommended. Quick, so a retry still fits the platform deadline. Recommended.' },
  { value: 'quality', label: 'High quality', help: 'The most accurate model other than Recommended. Slower, so a retry may run out of time.' },
  { value: 'none', label: 'Keep the current order', help: 'Only move Recommended to the front; the other models keep their order, so the old default becomes the first backup.' },
];

const ModelPair: React.FC<{ before?: string | null; after?: string | null }> = ({ before, after }) =>
  before === after ? (
    <Typography variant="body2" color="text.secondary">
      {modelLabel(after)}
    </Typography>
  ) : (
    <Typography variant="body2">
      <Box component="span" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
        {modelLabel(before)}
      </Box>{' '}
      → <b>{modelLabel(after)}</b>
    </Typography>
  );

type ApplyCall = (fallback: ApplyFallback, dryRun: boolean) => Promise<{ routes: ApplyRow[]; applied: number }>;

/** Apply test picks: Recommended becomes the default model and the chosen pick the first backup.
 *  Admins apply to platform routes in bulk; `call` applies to one function instead (`fromRun`: that run's picks). */
export const ApplyPicksDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  routes: (RouteRef & { name: string })[];
  call?: ApplyCall;
  fromRun?: boolean;
  onClose: () => void;
  onApplied: (applied: number) => void;
}> = ({ open, api, routes, call, fromRun, onClose, onApplied }) => {
  const single = !!call;
  const [fallback, setFallback] = useState<ApplyFallback>('budget');
  const [rows, setRows] = useState<ApplyRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refs = routes.map(r => ({ family_id: r.family_id, customer_id: r.customer_id }));
  const key = JSON.stringify(refs);

  useEffect(() => {
    if (open) {
      setFallback('budget');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !refs.length) return;
    let cancelled = false;
    setRows(null);
    (call ? call(fallback, true) : api.applyPicks(refs, fallback, true))
      .then(d => !cancelled && setRows(d.routes))
      .catch(e => !cancelled && setError(errorText(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key, fallback, api]);

  const changing = (rows || []).filter(r => r.changed && !r.error);
  const nameOf = (r: ApplyRow) => r.name || routes.find(x => x.family_id === r.family_id)?.name || r.family_id;

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = call
        ? await call(fallback, false)
        : await api.applyPicks(
            changing.map(r => ({ family_id: r.family_id, customer_id: r.customer_id })),
            fallback,
            false,
          );
      const failed = d.routes.filter(r => r.error);
      if (failed.length) setError(failed.map(r => `${nameOf(r)}: ${r.error}`).join(' '));
      onApplied(d.applied);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {single ? 'Use test picks' : 'Apply test picks'}
        {routes.length > 1 ? ` to ${routes.length} routes` : `: ${routes[0]?.name ?? ''}`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {single ? (
            <>
              The <b>Recommended</b> model from {fromRun ? 'this run' : 'the latest test that compared models'} becomes the default.
            </>
          ) : (
            <>
              Each route's <b>Recommended</b> model from its latest test becomes the default.
            </>
          )}{' '}
          Pick which model is tried first if a call fails:
        </Typography>
        <RadioGroup row value={fallback} onChange={e => setFallback(e.target.value as ApplyFallback)} sx={{ mb: 0.5 }}>
          {FALLBACK_OPTIONS.map(o => (
            <FormControlLabel key={o.value} value={o.value} control={<Radio size="small" />} label={o.label} />
          ))}
        </RadioGroup>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2 }}>
          {FALLBACK_OPTIONS.find(o => o.value === fallback)?.help}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {!rows ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{single ? 'Function' : 'Route'}</TableCell>
                <TableCell>Default model</TableCell>
                <TableCell>First backup</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.family_id} sx={{ opacity: r.changed && !r.error ? 1 : 0.6 }}>
                  <TableCell sx={{ fontWeight: 600 }}>{nameOf(r)}</TableCell>
                  <TableCell>{r.new_models ? <ModelPair before={r.current_models?.[0]} after={r.new_models[0]} /> : '–'}</TableCell>
                  <TableCell>{r.new_models ? <ModelPair before={r.current_models?.[1]} after={r.new_models[1]} /> : '–'}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color={r.error ? 'error' : 'text.secondary'}>
                      {[r.error, ...r.notes].filter(Boolean).join(' ')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {changing.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {single
              ? "This saves a new function version that serves traffic within about a minute. You can undo it from the function's Tests tab while the function is still as you left it."
              : 'These are live routes. Each changed route gets a new function version that serves traffic within about a minute. You can undo from this page while the route is still as you left it.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={apply} disabled={busy || !changing.length}>
          {busy
            ? 'Applying…'
            : !changing.length
              ? 'Nothing to change'
              : single
                ? 'Apply'
                : `Apply to ${changing.length} route${changing.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
